import { observer } from 'mobx-react-lite'
import type { TaxInfoGatheringViewModel } from '@/features/jobs/details/tax-type/info-gathering/TaxInfoGatheringViewModel'
import {
  DependentsInfoCardListItem,
  EmptyDependentsInfoCardListItem,
  EmptyPersonalInfoCardListItem,
  EmptySpousalInfoCardListItem,
  PersonalInfoCardListItem,
  PersonalInfoSection,
  SkeletonChecklistItemCardListItem,
  SpousalInfoCardListItem,
  TaxRefundMethodCardListItem,
  EmptyTaxRefundMethodCardListItem,
} from '@/features/jobs/details/tax-type/info-gathering/components/ChecklistItemsSections/index'
import { formatFullName } from '@/utils/full-name-util'
import { formatTaxDeliveryMethodType } from '@/features/tax-profile/tax-refund-methods/delivery-method-formatter'

interface PersonalInfoSectionContainerProps {
  vm: TaxInfoGatheringViewModel
}

/**
 * Personal Info Section Container
 */
export const PersonalInfoSectionContainer = observer(
  function PersonalInfoSectionContainer({
    vm,
  }: PersonalInfoSectionContainerProps) {
    if (vm.personalInfoSectionItemVms.length === 0) {
      return null
    }

    return (
      <PersonalInfoSection testId="personal-info-section">
        {vm.personalInfoSectionItemVms.map((itemVm) => {
          switch (itemVm.type) {
            case 'PersonalInfo':
              return itemVm.fetch.pending ? (
                <SkeletonChecklistItemCardListItem key={itemVm.type} />
              ) : itemVm.isCompleted ? (
                <PersonalInfoCardListItem
                  key={itemVm.type}
                  fullName={itemVm.fullName}
                  onAction={itemVm.showDialog}
                />
              ) : (
                <EmptyPersonalInfoCardListItem
                  key={itemVm.type}
                  onAction={itemVm.isReadOnly ? undefined : itemVm.showDialog}
                />
              )
            case 'SpousalInfo':
              return itemVm.fetch.pending ? (
                <SkeletonChecklistItemCardListItem key={itemVm.type} />
              ) : itemVm.isCompleted ? (
                <SpousalInfoCardListItem
                  key={itemVm.type}
                  fullName={itemVm.fullName}
                  onAction={itemVm.showDialog}
                />
              ) : (
                <EmptySpousalInfoCardListItem
                  key={itemVm.type}
                  onAction={itemVm.isReadOnly ? undefined : itemVm.showDialog}
                />
              )
            case 'DependentsInfo':
              return itemVm.fetch.pending ? (
                <SkeletonChecklistItemCardListItem key={itemVm.type} />
              ) : itemVm.isCompleted ? (
                <DependentsInfoCardListItem
                  key={itemVm.type}
                  fullNames={itemVm.providedDependents.map((d) =>
                    formatFullName(
                      d.personalInfo.firstName,
                      d.personalInfo.middleInitial,
                      d.personalInfo.lastName,
                    ),
                  )}
                  onAction={itemVm.showDependentsDialog}
                />
              ) : (
                <EmptyDependentsInfoCardListItem
                  key={itemVm.type}
                  onAction={
                    itemVm.isReadOnly ? undefined : itemVm.showDependentsDialog
                  }
                />
              )
            case 'PersonalTaxRefundMethod':
              return itemVm.fetch.pending ? (
                <SkeletonChecklistItemCardListItem key={itemVm.type} />
              ) : itemVm.isCompleted && itemVm.method ? (
                <TaxRefundMethodCardListItem
                  key={itemVm.type}
                  methodTitle={formatTaxDeliveryMethodType(
                    itemVm.method.deliveryMethod.type,
                  )}
                  onAction={itemVm.showTaxRefundMethodsDialog}
                />
              ) : (
                <EmptyTaxRefundMethodCardListItem
                  kind="personal"
                  key={itemVm.type}
                  onAction={
                    itemVm.isReadOnly
                      ? undefined
                      : itemVm.showTaxRefundMethodsDialog
                  }
                />
              )
          }
        })}
      </PersonalInfoSection>
    )
  },
)
